import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  DialogContent,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Divider,
  Avatar,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Link,
  Popover,
  PopoverOrigin,
  CircularProgress, useTheme,
  Grid,
} from '@mui/material'
import sprite_ from '../../sass/sprite.module.sass'

import { ResultTitle, useAccordionStyles } from '../../style/result'
import { SearchResult, Source } from '../../store/searcher/reducer'
import { getCorsImageUrl, RenderProfileData } from '../../component/searcher/RenderData'
import { search } from '../../function/data'
import { useDispatch } from '../../store'
import { prepareNameResult } from '../../store/searcher/service'
import CollapsibleTable from './NameApiCommon'
import WebmiiSocialResultsViewer from './WebmiiSocialResults'
import ThreadResultsViewer from './ThreadResultsViewer'
import RedditResultsViewer from './RedditResultsViewer'
import BreachDetectiveResultsViewer from './BreachDetectorViewer'

const searchExcludes = [
  'profile_id',
  'keyphrase',
  'keyword',
  'friendOf',
  'friendName',
  'friendProfileId',
  'friendImageUrl',
  'customrequestid',
  'type',
  'album_name',
  'setting=RABBIT_MQ_STATUS_MONITORING_QUEUE_NAME',
  'actualMainUrl',
  'profileCoverPhotoUrl',
]

function getFilterIcon(source: Source, sourceFilter: Source[]) {
  if (sourceFilter.includes(source)) return sprite_['social__large_' + source]
  else return sprite_['social__small_' + source]
}

function getAccordionStyle(idx: number, profileUrl: object) {
  return {
    cursor:
      profileUrl && Object.keys(profileUrl).length !== 0 ? 'pointer' : 'auto',
  }
}

type Props = {
  apiData: SearchResult
}

export function NameCommon(props: Props) {
  const { t } = useTranslation()
  const accordionClasses = useAccordionStyles()
  const theme = useTheme()

  const {apiData} = props
  const data = prepareNameResult(apiData)

  const hasApiResults = data.sources.some(source => 
    source.name === "x_api" || source.name === "fb_api" || source.name === "instagram_api" 
    || source.name === "vk_api" || source.name === "ok_api" || source.name === "linkedin_api"
      || source.name === "tiktok_api" || source.name === "blsk_api")
 

  const initState = React.useMemo(() => {
    return data.data ? data.data.map((_) => false) : []
  }, [data.data])

    const gLookIdResults = (data?.data ?? []).flatMap(
    (item: any) => item?.username_blkb?.results ?? [],
  )

  const webmiiSocialResults = (data?.data ?? [])
    .filter((item: any) => item?.source === 'webmii')
    .flatMap((item: any) => item?? [])
  
  const threadslResults = (data?.data ?? [])
    .filter((item: any) => item?.source === 'threads')
    .flatMap((item: any) => item?? [])

  const redditlResults = (data?.data ?? [])
    .filter((item: any) => item?.source === 'reddit')
    .flatMap((item: any) => item?? [])

  const breachDetectiveResults = (data?.data ?? [])
    .filter((item: any) => ['breach_name', 'breach_username'].includes(item?.from))
    .flatMap((item: any) => item?? [])

  //console.log('commonNames', data)

  const [accordionOpen, setAccordionOpen] = React.useState<boolean[]>(initState)
  const [filter, setFilter] = React.useState<Source[]>([])
  const [searchValue, setSearchValue] = React.useState('')

  let filteredData: any = React.useMemo(() => {
    let filtered =
      filter.length > 0
        ? data.data?.filter((el) => filter.includes(el.from))
        : data.data

    if (searchValue.length > 0) {
      filtered = filtered?.filter((row) =>
        search(searchValue, row, searchExcludes),
      )
    }

    return filtered
  }, [data.data, filter, searchValue])

  // const sources = React.useMemo(() => {
  //   return [...new Set(data.data?.map((el) => el.from))] || []
  // }, [data.data])

  const handleChangeAccordion =
    (idx: number) => (event: React.SyntheticEvent, expanded: boolean) => {
      setAccordionOpen((state) => {
        const newState = [...state]
        newState[idx] = expanded
        return newState
      })
    }

  filteredData = filteredData?.filter((el: any) => !!el?.profileData)
  //console.log('filtered data', filteredData)

  const dispatch = useDispatch()

  return (
    <>
      <Stack>
        {
          filteredData && filteredData?.length > 0 && <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            lineHeight={1}
            mb={3}
          >
            <ResultTitle>Details:</ResultTitle>
            <OutlinedInput
              value={searchValue}
              sx={{
                background: 'none',
              }}
              placeholder={t('searcher.name.popup.searchPlaceholder')}
              onChange={(e) => setSearchValue(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <Box className={sprite_.common__search} />
                </InputAdornment>
              }
              style={{ marginLeft: 'auto', width: 192, height: 38 }}
            />
          </Stack>
        }
        <>
          {filteredData.map((el: any, idx: number) => {
            return (
              <Accordion
                key={idx}
                classes={accordionClasses}
                square
                expanded={
                  el.profileData && Object.keys(el.profileData).length !== 0
                    ? accordionOpen[idx]
                    : false
                }
                onChange={handleChangeAccordion(idx)}
              >
                <AccordionSummary
                  style={getAccordionStyle(idx, el.profileData)}
                  sx={{
                    [theme.breakpoints.only("xs")]: {
                      justifyContent: 'flex-start',
                      margin: '3px',
                    },
                    margin: '10px',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={3}
                    width="100%"
                  >
                    <Avatar
                      src={getCorsImageUrl(el.profileImageUrl)}
                      sx={{
                        height: 75,
                        width: 75,
                        [theme.breakpoints.only("xs")]: {
                          width: 45,
                          height: 45,
                        },
                      }}
                    />
                    <Stack>
                      <Typography variant="bold">{el.profileName}</Typography>
                      <Typography variant="semiBold" color="secondary" sx={{
                        [theme.breakpoints.only("xs")]: {
                          fontSize: '10px',
                        },
                      }}>
                        {el.profileData && el.profileData['Profile Headline']}
                      </Typography>
                      <Typography variant="bold" color="secondary">
                        {el.profileCity}
                      </Typography>
                    </Stack>
                    <Stack
                      justifyContent="space-between"
                      alignItems="flex-end"
                      style={{ marginLeft: 'auto', height: '100%' }}
                      sx={{
                        [theme.breakpoints.only("xs")]: {
                          flexDirection: 'row',
                          width: '60px!important',
                          overflow: 'hidden',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          flexShrink: '0',
                        },
                      }}
                    >
                      <Box className={sprite_['social__small_' + el.from]} sx={{
                        [theme.breakpoints.only("xs")]: {
                          marginRight: '3px',
                        },
                      }} />
                      {el.profileUrl && (
                        <Link
                          variant="underlined"
                          href={el.profileUrl}
                          target="_blank"
                          sx={{
                            [theme.breakpoints.only("xs")]: {
                              fontSize: '10px',
                            },
                          }}
                        >
                          @{el.profile_id}
                        </Link>
                      )}
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {el.profileData && (
                    <RenderProfileData data={el.profileData} fullData={el} />
                  )}
                </AccordionDetails>
              </Accordion>
            )
          })}
          { (hasApiResults === true) && (
            <CollapsibleTable apiData={apiData}/> 
          ) }
          {gLookIdResults?.length > 0 && (
            <Box sx={{ width: "45%", mx: "auto" }}>
              {gLookIdResults?.length > 0 && (
                <Grid container>
                  <Grid item xs={12}>
                    <Grid container sx={{ borderBottom: "1px solid #ddd", pb: 1 }}>
                      <Grid item xs={6}>
                        <Typography fontWeight={600}>Application Name</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography fontWeight={600}>Category</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {gLookIdResults.map((el: any, idx: number) => (
                    <Grid item xs={12} key={el.url ?? idx}>
                      <Grid container sx={{ py: 1 }}>
                        <Grid item xs={6}>
                          {el.url ? (
                            <Typography
                              component="a"
                              href={el.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {el.name}
                            </Typography>
                          ) : (
                            <Typography>{el.name}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>{el.category}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {webmiiSocialResults && (
            <>
            <WebmiiSocialResultsViewer webmiiSocialResults={webmiiSocialResults}/>
            </>
          )}
          {threadslResults && (
            <>
            <ThreadResultsViewer threadslResults={threadslResults}/>
            </>
          )}
          {redditlResults && (
            <>
            <RedditResultsViewer redditResults={redditlResults}/>
            </>
          )}
          {breachDetectiveResults.length > 0 && (
            <BreachDetectiveResultsViewer
              breachDetectiveResults={breachDetectiveResults}
            />
          )}
        </>
      </Stack>
    </>
  )
}
